import { useEffect } from "react";
import appData from "data/app.json";

// WebMCP (Web Model Context Protocol) - eksperymentalny standard udostępniania
// narzędzi agentom AI w przeglądarce. API jest niestabilne i dostępne tylko za
// flagą w Chrome, więc wszystko jest defensywne: na przeglądarkach bez wsparcia
// hook nic nie robi (no-op) i nie może zepsuć strony.
export function useWebMCP() {
  useEffect(() => {
    // Detekcja wsparcia. Kolejność guardów ważna: typeof window najpierw,
    // żeby nie dotknąć `document` gdyby to jednak zbiegło się z SSR.
    if (typeof window === "undefined") return;
    if (typeof document === "undefined") return;
    const mc = document.modelContext || (window.navigator && window.navigator.modelContext);
    if (!mc || typeof mc.registerTool !== "function") return;

    const controller = new AbortController();
    const { signal } = controller;

    const services = [
      {
        id: "pozycjonowanie-stron",
        name: "Pozycjonowanie stron",
        url: "https://tensera.pl/pozycjonowanie-stron/",
        description:
          "SEO ukierunkowane na widoczność organiczną i realne zapytania - architektura topical authority, nie pojedyncze frazy.",
      },
      {
        id: "pozycjonowanie-sklepow",
        name: "Pozycjonowanie sklepów internetowych",
        url: "https://tensera.pl/pozycjonowanie-sklepow/",
        description:
          "SEO e-commerce w skali: kategorie, masowe opisy produktów pipeline'em AI z kontrolą jakości, dane strukturalne.",
      },
      {
        id: "pozycjonowanie-ai",
        name: "Pozycjonowanie w AI (GEO / Generative Engine Optimization)",
        url: "https://tensera.pl/pozycjonowanie-ai/",
        description:
          "Budowa obecności i cytowań marki w ChatGPT, Gemini, AI Overviews i Perplexity - audyt, budowa marki jako encji, monitoring.",
      },
      {
        id: "audyt",
        name: "Audyt SEO i AI",
        url: "https://tensera.pl/audyt/",
        description:
          "Diagnoza techniczna serwisu oraz baseline widoczności marki w modelach LLM. Raport z priorytetami i omówienie.",
      },
    ];

    async function registerTenseraTools() {
      try {
        await mc.registerTool(
          {
            name: "tensera_get_services",
            description:
              "Zwraca oficjalny katalog usług agencji Tensera: pozycjonowanie stron i sklepów, pozycjonowanie w AI (GEO) oraz audyt SEO i AI.",
            inputSchema: {
              type: "object",
              properties: {},
              additionalProperties: false,
            },
            execute: async () => ({
              company: "Tensera",
              website: "https://tensera.pl",
              tagline:
                "Widoczność, która zamienia się w klientów - w Google i w odpowiedziach AI.",
              contact: "hello@tensera.pl",
              services,
            }),
          },
          { signal }
        );

        await mc.registerTool(
          {
            name: "tensera_request_audit",
            description:
              "Przesyła do agencji Tensera zapytanie o audyt SEO/AI lub wycenę. Wymaga adresu e-mail i adresu strony klienta.",
            inputSchema: {
              type: "object",
              properties: {
                email: {
                  type: "string",
                  description: "Adres e-mail osoby zamawiającej kontakt.",
                },
                website_url: {
                  type: "string",
                  description: "Adres URL strony klienta do zbadania.",
                },
                notes: {
                  type: "string",
                  description: "Dodatkowe informacje lub specyfikacja potrzeb.",
                },
              },
              required: ["email", "website_url"],
              additionalProperties: false,
            },
            execute: async ({ email, website_url, notes }) => {
              if (!appData.web3formsKey) {
                return {
                  status: "error",
                  message:
                    "Kanał zgłoszeń jest chwilowo niedostępny. Napisz na hello@tensera.pl.",
                };
              }
              try {
                const res = await fetch("https://api.web3forms.com/submit", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({
                    access_key: appData.web3formsKey,
                    subject: "[tensera.pl] Zapytanie o audyt przez agenta AI (WebMCP)",
                    from_name: "Agent WebMCP",
                    temat: "Audyt / wycena (agent WebMCP)",
                    email,
                    strona_www: website_url,
                    wiadomosc: notes || "(brak dodatkowych informacji)",
                    zrodlo: "WebMCP / agent AI",
                  }),
                });
                const json = await res.json();
                if (json && json.success) {
                  return {
                    status: "success",
                    message: `Zgłoszenie dla domeny ${website_url} zostało przyjęte. Odezwiemy się na adres ${email}.`,
                  };
                }
                return {
                  status: "error",
                  message:
                    "Nie udało się przyjąć zgłoszenia. Napisz bezpośrednio na hello@tensera.pl.",
                };
              } catch (err) {
                return {
                  status: "error",
                  message:
                    "Błąd sieci przy wysyłce zgłoszenia. Napisz na hello@tensera.pl.",
                };
              }
            },
          },
          { signal }
        );

        // eslint-disable-next-line no-console
        console.log("[WebMCP] Narzędzia Tensera zarejestrowane.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[WebMCP] Rejestracja narzędzi nieudana:", err);
      }
    }

    registerTenseraTools();

    return () => controller.abort();
  }, []);
}
